import "./index.css";
import "./typeViber.css";
import "./typeWhatsapp.css";
import "./typeSms.css";
import "./typeEmail.css";
import "./typePush.css";

import appIconLight from "Images/logo_small_light.png";
import appIconDark from "Images/logo_small_dark.png";

import React from "react";
import BaseComponent from "Core/components/BaseComponent";
import PropTypes from "prop-types";
import {
	MESSAGE_PREVIEW_BUTTON_TYPE,
	MESSAGE_PREVIEW_TYPE,
	MESSAGE_PREVIEW_TYPE_ICONS,
	MESSAGE_PREVIEW_TYPES
} from './const';
import * as optionObjects from "./optionObjects";
import {find, get, pick} from 'lodash';
import {getArray, getNumber, getString, isset, trimArray} from 'Core/helpers/data';
import {cssStyleStringToObject, nl2br} from "Core/helpers/string";
import Html from "Core/components/display/Html";
import DateLabel from "Core/components/display/DateLabel";
import {SKIN_MODE} from "Core/const/global";
import {getSkin} from "Core/helpers/skin";
import Label from "Core/components/display/Label";
import Icon from 'Core/components/display/Icon';
import {openDialog} from 'Core/helpers/dialog';
import TextDialog from 'Core/components/dialogs/TextDialog';

class MessagePreview extends BaseComponent {
	constructor(props) {
		super(props, {
			translationPath: "MessagePreview",
			domPrefix: 'message-preview-component',
			optimizedUpdate: true,
			updateOnSkinChange: true,
		});

		// Dialog methods
		this.openMediaPreviewDialog = this.openMediaPreviewDialog.bind(this);
		
		// Render methods
		this.updateCssVars = this.updateCssVars.bind(this);
	}
	
	componentDidMount() {
		super.componentDidMount();
		
		this.updateCssVars();
	}


	componentDidUpdate(prevProps, prevState, snapshot) {
		this.updateCssVars();
	}

	/**
	 * Opens a dialog to preview media content.
	 *
	 * @param {Object} content - The media content to be displayed in the dialog.
	 * @return {void} This method does not return anything.
	 */
	openMediaPreviewDialog(content) {
		const dialogGUIID = openDialog('message-preview-media-preview-dialog', TextDialog, {
			content
		}, {
			closeOnClickOutside: true,
			closeOnEscape: true,
			hideCloseBtn: false,
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}

	/**
	 * Update CSS custom properties (variables) for the wrapper div
	 */
	updateCssVars() {
		const {
			deviceWidth, deviceAspectRatio, deviceBorderRadius, deviceBezel, deviceBezelColor, deviceBorder,
			deviceScreenBorder, deviceScreenBackground, fontSize, messageBackground, messageBorder, buttonBackground, 
			buttonColor
		} = this.props;
		const elem = this.getDomElement();
		
		if (elem) {
			if (isset(deviceWidth)) elem.style.setProperty('--device-width', deviceWidth);
			if (isset(deviceAspectRatio)) elem.style.setProperty('--device-aspect-ratio', deviceAspectRatio);
			if (isset(deviceBorderRadius)) elem.style.setProperty('--device-border-radius', deviceBorderRadius);
			if (isset(deviceBezel)) elem.style.setProperty('--device-bezel', deviceBezel);
			if (isset(deviceBezelColor)) elem.style.setProperty('--device-bezel-color', deviceBezelColor);
			if (isset(deviceBorder)) elem.style.setProperty('--device-border', deviceBorder);
			if (isset(deviceScreenBorder)) elem.style.setProperty('--device-screen-border', deviceScreenBorder);
			if (isset(deviceScreenBackground)) elem.style.setProperty('--device-screen-background', deviceScreenBackground);
			if (isset(fontSize)) elem.style.setProperty('--device-screen-font-size', fontSize);
			if (isset(messageBackground)) elem.style.setProperty('--device-message-background', messageBackground);
			if (isset(messageBorder)) elem.style.setProperty('--device-message-border', messageBorder);
			if (isset(buttonBackground)) elem.style.setProperty('--device-button-background', buttonBackground);
			if (isset(buttonColor)) elem.style.setProperty('--device-button-color', buttonColor);
		}
	}
	
	render() {
		const {
			className, messageType, defaultStyle, showMessageTypeHeader, messageTypeHeaderRecipient, messageTime, 
			messageTimeFormat
		} = this.props;
		const optionsClass = get(optionObjects, `MessagePreviewOptions${messageType}`);
		let messageOptions = null;
		if (optionsClass) {
			messageOptions = pick(
				get(this.props, 'messageOptions'),
				Object.getOwnPropertyNames(new optionsClass())
			);
		}
		
		const subjectType = getString(messageOptions, 'subjectType');
		let subject = get(messageOptions, 'subject', null);
		const subjectLimit = getNumber(messageOptions, 'subjectLimit', null);
		if (subjectLimit > 0 && getString(subject).length > subjectLimit) subject = subject.slice(0, subjectLimit)+'...';
		
		let body = get(messageOptions, 'body', null);
		const bodyLimit = getNumber(messageOptions, 'bodyLimit', null);
		if (bodyLimit > 0 && getString(body).length > bodyLimit) body = body.slice(0, bodyLimit)+'...';
		
		const video = get(messageOptions, 'video', null);
		const videoThumbnail = getString(messageOptions, 'videoThumbnail');
		const image = get(messageOptions, 'image', null);
		const carouselItems = getArray(messageOptions, 'carouselItems');
		const appIcon = (getSkin() === SKIN_MODE.DARK ? appIconDark : appIconLight);

		const footer = get(messageOptions, 'footer', null);

		const link = get(messageOptions, 'link', null);
		const linkText = getString(messageOptions, 'linkText');
		const buttons = getArray(messageOptions, 'buttons');
		
		return (
			<div 
				id={this.getDomId()} 
				className={`${this.getOption('domPrefix')} ${className} message-type-${messageType}`}
			>
				<div className="preview">
					<div className="speaker" />
					<div className="screen-wrapper">
						{showMessageTypeHeader ?
							<div className="type-header">
								{!!getString(MESSAGE_PREVIEW_TYPE_ICONS, messageType) ?
									<><Icon {...MESSAGE_PREVIEW_TYPE_ICONS[messageType]} />&nbsp;</> :
									null
								}
								<Label content={this.t(messageType, 'constants.messageType')}/>
								{!!messageTypeHeaderRecipient ?
									!!messageType ?
										<>: <Label content={messageTypeHeaderRecipient} /></>:
										<Label content={messageTypeHeaderRecipient} />
									: null
								}
							</div>
							: null
						}
						<div className="screen">
							<div className="avatar" />
							<div className="message">
								<div className="header">
									{
										messageType === MESSAGE_PREVIEW_TYPE.PUSH ?
											<div className="app-name">
												<img className="app-icon" src={appIcon} alt="" />
												<Label content={this.t('app_name')} />
												<span className="time">&middot;&nbsp;&nbsp;
													{isset(messageTime) ?
														<DateLabel inputDate={messageTime} outputFormat={messageTimeFormat} /> : 
														'2m'
													}
												</span>
											</div>
										: messageType === MESSAGE_PREVIEW_TYPE.WHATSAPP ?
											(subjectType === 'IMAGE' || subjectType === 'GIF') ?
												<div
													className="image"
													onClick={() => this.openMediaPreviewDialog(<img src={subject} alt="" />)}
												>
													<img src={subject} alt="" />
												</div>
											: subjectType === 'VIDEO' ?
													<div
														className="video"
														onClick={() => this.openMediaPreviewDialog(<video src={subject} controls />)}
													>
														<video src={subject} />
													</div>
											: subjectType === 'DOCUMENT' ?
												<a 
													className="document" 
													href={subject} 
													title={subject.split('/').pop().split('.').slice(0, -1).join('.')}
												>
													<Icon symbol="file-pdf-o" />
													<div className="file-info">
														<div className="file-name">{
															subject.split('/').pop().split('.').slice(0, -1).join('.')
														}</div>
														<div className="file-type">PDF</div>
													</div>
												</a>
											:
												<h2>{subject}</h2>		
										: subject ?
											<h2>{subject}</h2>
										: null
									}
								</div>
								
								<div className="body">
									{!!video ? 
										<div 
											className="video" 
											onClick={() => this.openMediaPreviewDialog(<video src={video} controls />)}
										>
											<img src={videoThumbnail} alt="" />
										</div> 
										: null
									}
									<div className="content">
										{messageType === MESSAGE_PREVIEW_TYPE.PUSH ? subject ? <h2>{subject}</h2> : null : null}
										{body ?
											messageType === MESSAGE_PREVIEW_TYPE.EMAIL ?
												<Html
													element="div"
													elementProps={{style: cssStyleStringToObject(getString(defaultStyle))}}
													content={body}
												/>
												:
												<Html
													element="p"
													elementProps={{style: cssStyleStringToObject(getString(defaultStyle))}}
													content={nl2br(body)}
												/>
											: null}
									</div>
									{image ? 
										<div 
											className="image"
											onClick={() => this.openMediaPreviewDialog(<img src={image} alt="" />)}
										>
											<img src={image} alt="" />
										</div> 
										: null
									}
								</div>
								
								{!!footer ?
									<div className="footer">
										<Html
											element="div"
											elementProps={{style: cssStyleStringToObject(getString(defaultStyle))}}
											content={nl2br(footer)}
										/>
									</div>
									: null	
								}
								
								{link || linkText ?
									messageType === MESSAGE_PREVIEW_TYPE.VIBER && carouselItems.length > 0 ? null : 
									<div className="actions">
										<a
											href={link ? link : (!!video ? video : undefined)}
											target="_blank"
											rel="noopener noreferrer"
											className={`button solid ${messageType} no-select`}
											download={!!video}
										>{getString(messageOptions, 'linkText')}</a>
									</div>
									: null
								}
								
								<div className="time">
									<DateLabel 
										inputDate={isset(messageTime) ? messageTime : new Date()} 
										outputFormat={messageTimeFormat} 
									/>
								</div>

								{buttons.length > 0 ?
									<div className="buttons">
										{buttons.map((button, bIdx) =>
											button.type === MESSAGE_PREVIEW_BUTTON_TYPE.QUICK_REPLY ?
												<Label
													key={bIdx}
													element="span"
													elementProps={{
														className: `button none ${button.type} no-select`,
														style: cssStyleStringToObject(getString(defaultStyle)),
													}}
													icon="reply"
													content={getString(button, 'text')}
												/>
											: button.type === MESSAGE_PREVIEW_BUTTON_TYPE.URL ?
												<Label
													key={bIdx}
													element="a"
													elementProps={{
														className: `button none ${button.type} no-select`,
														style: cssStyleStringToObject(getString(defaultStyle)),
														href: getString(button, 'url'),
														target: '_blank',
														rel: 'noopener noreferrer',
														title: getString(button, 'url')
													}}
													icon="external-link"
													content={getString(button, 'text')}
												/>
											: button.type === MESSAGE_PREVIEW_BUTTON_TYPE.PHONE ?
												<Label
													key={bIdx}
													element="a"
													elementProps={{
														className: `button none ${button.type} no-select`,
														style: cssStyleStringToObject(getString(defaultStyle)),
														href: `tel:${getString(button, 'phone_number')}`,
														target: '_blank',
														rel: 'noopener noreferrer',
														title: getString(button, 'phone_number')
													}}
													icon="phone"
													content={getString(button, 'text')}
												/>
											: null
										)}
									</div>
									: null
								}
							</div>
							
							{messageType === MESSAGE_PREVIEW_TYPE.VIBER && carouselItems.length > 0 ?
								<div className="carousel">
									{carouselItems.map(item => (
										<div key={item.GUIID} className="carousel-item">
											{!!item.image ?
												<img 
													className={`item-section item-image`} 
													src={item.image} 
													alt={getString(item, 'image')}
												/>
												:
												<Label
													element="div"
													elementProps={{className: 'item-section item-image empty'}}
													icon="image"
												/>
											}

											<div className="item-content">
												{!!item.title ?
													<div className="item-section item-title"><Label content={item.title} /></div>
													: null
												}

												{getArray(item, 'buttons').length > 0 ?
													<div className={`item-buttons`}>
														{!!getString(find(getArray(item, 'buttons'), {ordinal: 1}), 'label') ?
															<a
																href={
																	getString(find(getArray(item, 'buttons'), {ordinal: 1}), 'action') ||
																	'#'
																}
																target="_blank"
																rel="noopener noreferrer"
																className={`button item-button button-primary`}
															>
																<Label content={
																	getString(find(getArray(item, 'buttons'), {ordinal: 1}), 'label') ||
																	'...'
																} />
															</a>
															:
															<Label
																element="div"
																elementProps={{className: 'button item-button button-primary'}}
																content={
																	getString(find(getArray(item, 'buttons'), {ordinal: 1}), 'label') ||
																	'...'
																}
															/>
														}

														{!!getString(find(getArray(item, 'buttons'), {ordinal: 2}), 'label') ?
															<a
																href={
																	getString(find(getArray(item, 'buttons'), {ordinal: 2}), 'action') ||
																	'#'
																}
																target="_blank"
																rel="noopener noreferrer"
																className={`button item-button button-secondary`}
															>
																<Label content={
																	getString(find(getArray(item, 'buttons'), {ordinal: 2}), 'label') ||
																	'...'
																} />
															</a>
															: null
														}
													</div>
													: null
												}
											</div>
										</div>
									))}
								</div>
								: null
							}
						</div>
					</div>
				</div>
			</div>
		);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
MessagePreview.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
	
	// @type {MessagePreviewMessageType|string}
	messageType: PropTypes.oneOfType([PropTypes.oneOf(MESSAGE_PREVIEW_TYPES), PropTypes.string]),
	// @type {
	// 	MessagePreviewOptionsSMS |
	// 	MessagePreviewOptionsEmail |
	// 	MessagePreviewOptionsViber |
	// 	MessagePreviewOptionsPUSH_NOTIFICATION
	// }
	messageOptions: PropTypes.object,
	
	// Device frame width in px
	deviceWidth: PropTypes.number,
	// Aspect ratio css value
	// @example '9/16' - Portrait phone.
	deviceAspectRatio: PropTypes.string,
	// Device frame outer border radius
	// @note Inner border radius will be automatically calculated.
	deviceBorderRadius: PropTypes.number,
	// Device frame bezel in px
	deviceBezel: PropTypes.number,
	// Device frame bezel background color
	deviceBezelColor: PropTypes.string,
	// Device frame outer border CSS value
	// @example '1px solid var(--border-color-main)'
	deviceBorder: PropTypes.string,
	// Device frame inner (screen) border CSS value
	// @example '1px solid var(--border-color-main)'
	deviceScreenBorder: PropTypes.string,
	// Device frame inner (screen) background CSS value
	deviceScreenBackground: PropTypes.string,
	// Device frame inner (screen) font size CSS value
	fontSize: PropTypes.string,
	// Message background CSS value
	messageBackground: PropTypes.string,
	// Message border CSS value
	// @example '1px solid var(--background-color-secondary)'
	messageBorder: PropTypes.string,
	// Action button background CSS value
	buttonBackground: PropTypes.string,
	// Action button label color CSS value
	buttonColor: PropTypes.string,
	// Flag that specifies if the message type header is rendered at the top of the device screen
	showMessageTypeHeader: PropTypes.bool,
	// Recipient name that will be rendered in the message header (if enabled) after the message type.
	// @note This is used only if the 'showMessageTypeHeader' prop is true.
	messageTypeHeaderRecipient: PropTypes.string,
	// Date value that will be rendered in the message preview as sent time
	// @note If undefined, current time will be used.
	messageTime: PropTypes.object,
	// Format used to render message time
	// @note This is used only if the 'messageTime' prop is defined.
	messageTimeFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
	
	// Default CSS style for the preview
	// @example 'font-family: ariel, sans-serif;'
	defaultStyle: PropTypes.string,
};

/**
 * Define component default values for own props
 */
MessagePreview.defaultProps = {
	id: '',
	className: '',
	messageType: '',
	messageOptions: null,
	defaultStyle: 'font-family: ariel, sans-serif;',
	showMessageTypeHeader: false,
	messageTypeHeaderRecipient: '',
	messageTimeFormat: 'HH:mm',
};

export * from "./const";
export * from "./optionObjects";
export default MessagePreview;